<template>
  <div class="point-cluster-demo" v-loading="loading">
    <div class="demo-layout">
      <section class="map-panel">
        <div class="map-panel__toolbar">
          <div class="toolbar-copy">
            <h3>点聚合预览</h3>
            <p>基于高德地图 `MarkerClusterer` 展示海量点位聚合、状态筛选和自定义聚合样式。</p>
          </div>
          <div class="toolbar-actions">
            <el-button size="small" icon="el-icon-refresh" @click="generateRandomPoints">刷新点位</el-button>
            <el-button size="small" icon="el-icon-location-outline" @click="locateCurrentPosition">定位</el-button>
            <el-button size="small" icon="el-icon-full-screen" @click="fitMapView">自适应</el-button>
          </div>
        </div>

        <div ref="mapContainer" class="map-stage"></div>

        <div class="map-legend">
          <div class="legend-item">
            <span class="legend-dot online"></span>
            <span>在线点位</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot offline"></span>
            <span>离线点位</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot warning"></span>
            <span>告警点位</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot cluster"></span>
            <span>聚合气泡</span>
          </div>
        </div>
      </section>

      <aside class="side-panel">
        <div class="panel-card stats-card">
          <div class="panel-card__title">数据概览</div>
          <div class="stats-grid">
            <div class="stats-item">
              <span>当前点位</span>
              <strong>{{ filteredPoints.length }}</strong>
            </div>
            <div class="stats-item">
              <span>在线</span>
              <strong class="online">{{ onlineCount }}</strong>
            </div>
            <div class="stats-item">
              <span>离线</span>
              <strong class="offline">{{ offlineCount }}</strong>
            </div>
            <div class="stats-item">
              <span>告警</span>
              <strong class="warning">{{ warningCount }}</strong>
            </div>
          </div>
        </div>

        <div class="panel-card">
          <div class="panel-card__title">聚合控制</div>

          <div class="field-block">
            <div class="field-label">聚合阈值</div>
            <el-slider
              v-model="clusterNum"
              :min="2"
              :max="20"
              show-input
              @change="refreshCluster"
            />
            <div class="field-tip">当相邻点位数量达到该阈值时，开始渲染聚合气泡。</div>
          </div>

          <div class="field-block">
            <div class="field-label">点位数量</div>
            <el-select v-model="pointCount" style="width: 100%" @change="generateRandomPoints">
              <el-option label="100 个点" :value="100" />
              <el-option label="200 个点" :value="200" />
              <el-option label="500 个点" :value="500" />
              <el-option label="1000 个点" :value="1000" />
            </el-select>
          </div>

          <div class="field-block">
            <div class="field-label">状态筛选</div>
            <el-checkbox-group v-model="statusFilter" @change="applyFilters">
              <el-checkbox label="online">在线</el-checkbox>
              <el-checkbox label="offline">离线</el-checkbox>
              <el-checkbox label="warning">告警</el-checkbox>
            </el-checkbox-group>
          </div>
        </div>

        <div class="panel-card">
          <div class="panel-card__title">样式配置</div>

          <div class="field-block">
            <div class="field-label">主题色</div>
            <el-select v-model="currentTheme" style="width: 100%" @change="refreshCluster">
              <el-option label="默认蓝" value="default" />
              <el-option label="绿色主题" value="green" />
              <el-option label="橙色主题" value="orange" />
              <el-option label="红色主题" value="red" />
              <el-option label="深色主题" value="dark" />
            </el-select>
          </div>

          <div class="field-block">
            <div class="field-label">聚合尺寸</div>
            <el-radio-group v-model="clusterSize" size="small" @change="refreshCluster">
              <el-radio-button label="small">小</el-radio-button>
              <el-radio-button label="medium">中</el-radio-button>
              <el-radio-button label="large">大</el-radio-button>
            </el-radio-group>
          </div>
        </div>
      </aside>
    </div>

    <el-dialog
      :visible.sync="detailDialogVisible"
      width="420px"
      :title="selectedPoint ? selectedPoint.name : '点位详情'"
      append-to-body
    >
      <div v-if="selectedPoint" class="detail-panel">
        <div class="detail-row">
          <span class="detail-label">状态</span>
          <el-tag :type="getStatusTagType(selectedPoint.status)">
            {{ getStatusText(selectedPoint.status) }}
          </el-tag>
        </div>
        <div class="detail-row">
          <span class="detail-label">经度</span>
          <span>{{ formatCoord(selectedPoint, 0) }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">纬度</span>
          <span>{{ formatCoord(selectedPoint, 1) }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">说明</span>
          <span>{{ selectedPoint.description }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">更新时间</span>
          <span>{{ selectedPoint.updateTime }}</span>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import AMapLoader from '@amap/amap-jsapi-loader'

const AMAP_KEY = '0826878f934aa6f773237d9c82dfcf45'
const ALL_STATUSES = ['online', 'offline', 'warning']

function randomBetween(min, max) {
  return min + Math.random() * (max - min)
}

function hexToRgba(color, alpha) {
  const hex = String(color || '#1890ff').replace('#', '')
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function createMarkerHtml(point, statusConfig) {
  const statusKey = point && point.status ? point.status : ''
  const config = (statusConfig && statusConfig[statusKey]) || { color: '#3478f6' }
  return `
    <div style="
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: ${config.color};
      border: 3px solid rgba(255,255,255,0.95);
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    "></div>
  `
}

function createClusterHtml({ count, color, size, textColor }) {
  const outer = size + 16
  const middle = size + 8
  const label = count > 99 ? '99+' : count
  const fontSize = count > 99 ? 13 : 15

  return `
    <div style="
      position: relative;
      width: ${outer}px;
      height: ${outer}px;
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <div style="
        position: absolute;
        width: ${outer}px;
        height: ${outer}px;
        border-radius: 50%;
        background: ${hexToRgba(color, 0.22)};
      "></div>
      <div style="
        position: absolute;
        width: ${middle}px;
        height: ${middle}px;
        border-radius: 50%;
        background: ${hexToRgba(color, 0.48)};
      "></div>
      <div style="
        position: absolute;
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        background: ${color};
        color: ${textColor};
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${fontSize}px;
        font-weight: 700;
        box-shadow: 0 8px 18px rgba(0,0,0,0.18);
      ">${label}</div>
    </div>
  `
}

export default {
  name: 'PointCluster',
  data() {
    return {
      // 页面加载态（地图 SDK 加载/聚合刷新时）
      loading: false,

      // 高德 SDK 实例（AMapLoader.load 返回）
      AMap: null,
      // 地图实例
      map: null,

      // 聚合实例（MarkerCluster 或 MarkerClusterer）
      clusterer: null,
      // 聚合构造器（兼容不同插件导出名）
      clusterCtor: null,

      // 普通点位模式下的 Marker 集合（聚合失败/降级时使用）
      pointMarkers: [],

      // 点位生成的中心（默认天安门附近；刷新时会用当前地图中心覆盖）
      centerCoordinate: [116.397428, 39.90923],
      // 当前地图缩放级别
      currentZoom: 11,

      // 聚合阈值：达到该数量后才聚合
      clusterNum: 5,
      // 随机点位数量
      pointCount: 100,
      // 状态筛选（online/offline/warning）
      statusFilter: ['online', 'offline', 'warning'],

      // 聚合主题
      currentTheme: 'default',
      // 聚合气泡尺寸
      clusterSize: 'medium',

      // 全量点位数据
      allPoints: [],
      // 过滤后的点位数据（真正用于渲染）
      filteredPoints: [],

      // 当前选中的点位（用于详情弹窗）
      selectedPoint: null,
      // 点位详情弹窗开关
      detailDialogVisible: false,

      themes: {
        default: { color: '#3478f6', textColor: '#ffffff' },
        green: { color: '#4caf50', textColor: '#ffffff' },
        orange: { color: '#ff9800', textColor: '#ffffff' },
        red: { color: '#f56c6c', textColor: '#ffffff' },
        dark: { color: '#303133', textColor: '#ffffff' }
      },
      clusterSizeMap: {
        small: 36,
        medium: 44,
        large: 54
      },
      statusConfig: {
        online: { text: '在线', color: '#4caf50' },
        offline: { text: '离线', color: '#909399' },
        warning: { text: '告警', color: '#e6a23c' }
      }
    }
  },
  computed: {
    // 统计：在线数量
    onlineCount() {
      return this.filteredPoints.filter(item => item.status === 'online').length
    },
    // 统计：离线数量
    offlineCount() {
      return this.filteredPoints.filter(item => item.status === 'offline').length
    },
    // 统计：告警数量
    warningCount() {
      return this.filteredPoints.filter(item => item.status === 'warning').length
    },
    // 当前主题配置
    currentThemeConfig() {
      return this.themes[this.currentTheme] || this.themes.default
    }
  },
  mounted() {
    this.initMap()
  },
  beforeDestroy() {
    this.destroyClusterer()
    if (this.map) {
      this.map.destroy()
      this.map = null
    }
  },
  methods: {
    // 格式化经纬度显示，避免坐标为空时模板报错。
    formatCoord(point, index) {
      if (!point || !point.coordinate || !Array.isArray(point.coordinate)) return '-'
      const value = Number(point.coordinate[index])
      if (!Number.isFinite(value)) return '-'
      return value.toFixed(6)
    },

    // 获取当前地图中心点（优先使用 map.getCenter）。
    getMapCenterCoordinate() {
      if (this.map && this.map.getCenter) {
        const center = this.map.getCenter()
        if (center) {
          if (typeof center.getLng === 'function' && typeof center.getLat === 'function') {
            return [center.getLng(), center.getLat()]
          }
          if (typeof center.lng === 'number' && typeof center.lat === 'number') {
            return [center.lng, center.lat]
          }
        }
      }
      return this.centerCoordinate
    },

    // 将各种来源的数据（extData / cluster data / marker extData）统一成点位对象，确保弹窗字段齐全。
    normalizePoint(pointLike, fallbackLngLat) {
      if (!pointLike) return null

      const raw = pointLike && pointLike.extData ? pointLike.extData : pointLike
      const pickLngLat = value => {
        if (!value) return null
        if (Array.isArray(value) && value.length >= 2) return [Number(value[0]), Number(value[1])]
        if (typeof value.getLng === 'function' && typeof value.getLat === 'function') return [value.getLng(), value.getLat()]
        if (typeof value.lng === 'number' && typeof value.lat === 'number') return [value.lng, value.lat]
        return null
      }

      const coordinate =
        pickLngLat(raw.coordinate) ||
        pickLngLat(raw.lnglat) ||
        pickLngLat(raw.position) ||
        pickLngLat(fallbackLngLat) ||
        this.centerCoordinate

      return {
        id: raw.id || '',
        name: raw.name || '未命名点位',
        status: raw.status || 'offline',
        description: raw.description || '',
        updateTime: raw.updateTime || '',
        coordinate
      }
    },

    // 初始化地图：加载 SDK、创建 Map、绑定控制器。
    async initMap() {
      this.loading = true
      try {
        const AMap = await AMapLoader.load({
          key: AMAP_KEY,
          version: '2.0',
          plugins: ['AMap.Scale', 'AMap.ToolBar', 'AMap.MarkerClusterer']
        })

        this.AMap = AMap
        this.clusterCtor = AMap.MarkerCluster || AMap.MarkerClusterer || null
        this.map = new AMap.Map(this.$refs.mapContainer, {
          zoom: this.currentZoom,
          center: this.centerCoordinate,
          resizeEnable: true,
          viewMode: '2D'
        })

        this.map.addControl(new AMap.Scale())
        this.map.addControl(new AMap.ToolBar({ position: 'RB' }))
        this.generateRandomPoints()
      } catch (error) {
        console.error('Failed to init AMap:', error)
        this.$toast.error('高德地图加载失败')
      } finally {
        this.loading = false
      }
    },

    // 生成随机点位：围绕“当前地图中心”生成，保证定位后刷新也能看到点位。
    generateRandomPoints() {
      const center = this.getMapCenterCoordinate()
      this.centerCoordinate = center

      const points = []

      for (let index = 0; index < this.pointCount; index += 1) {
        const status = ALL_STATUSES[Math.floor(Math.random() * ALL_STATUSES.length)]
        points.push({
          id: `cluster_point_${Date.now()}_${index}`,
          name: `点位 ${index + 1}`,
          coordinate: [
            center[0] + randomBetween(-0.12, 0.12),
            center[1] + randomBetween(-0.12, 0.12)
          ],
          status,
          description: `这是点位 ${index + 1} 的业务说明`,
          updateTime: new Date().toLocaleString('zh-CN')
        })
      }

      this.allPoints = points
      this.applyFilters()
      this.$toast.success(`已生成 ${points.length} 个点位`)
    },
    applyFilters() {
      this.filteredPoints = this.allPoints.filter(item => this.statusFilter.includes(item.status))
      this.refreshCluster()
    },

    // 创建普通 Marker（用于聚合失败时的降级模式）。
    createPointMarker(point) {
      const normalized = this.normalizePoint(point)
      const marker = new this.AMap.Marker({
        position: normalized.coordinate,
        content: createMarkerHtml(normalized, this.statusConfig),
        offset: new this.AMap.Pixel(-12, -12),
        extData: normalized,
        title: normalized.name
      })

      marker.on('click', () => {
        this.selectedPoint = normalized
        this.detailDialogVisible = true
      })

      return marker
    },

    // 聚合插件的单点渲染回调：为聚合内部 marker 设置内容、extData 和点击事件。
    renderPointMarker(context) {
      if (!context || !context.marker) return

      // MarkerCluster 的 data 结构在不同版本/插件实现中不完全一致，这里做兼容兜底。
      const raw = context.data || null
      const pointLike =
        (raw && raw.extData) ||
        (raw && raw.data) ||
        (raw && raw.origin) ||
        raw ||
        null
      const normalized = this.normalizePoint(pointLike, raw && raw.lnglat)
      if (!normalized) return

      const marker = context.marker
      marker.setOffset(new this.AMap.Pixel(-12, -12))
      marker.setContent(createMarkerHtml(normalized, this.statusConfig))
      if (marker.setTitle && normalized.name) marker.setTitle(normalized.name)

      if (marker.setExtData) {
        marker.setExtData(normalized)
      }

      if (!marker.__pointClickBound) {
        marker.on('click', () => {
          const detail = marker.getExtData ? marker.getExtData() : normalized
          this.selectedPoint = this.normalizePoint(detail) || normalized
          this.detailDialogVisible = true
        })
        marker.__pointClickBound = true
      }
    },
    renderPlainMarkers() {
      this.pointMarkers = this.filteredPoints.map(point => this.createPointMarker(point))
      this.map.add(this.pointMarkers)
    },

    // 销毁聚合与点位，避免多次刷新叠加覆盖物。
    destroyClusterer() {
      if (this.clusterer) {
        if (this.clusterer.setMap) this.clusterer.setMap(null)
        if (this.clusterer.clearMarkers) this.clusterer.clearMarkers()
        this.clusterer = null
      }

      if (this.pointMarkers.length && this.map) {
        this.map.remove(this.pointMarkers)
      }

      if (this.map && this.map.clearMap) {
        this.map.clearMap()
      }

      this.pointMarkers = []
    },
    refreshCluster() {
      if (!this.map || !this.AMap) return

      this.destroyClusterer()

      if (!this.filteredPoints.length) return

      if (!this.clusterCtor) {
        this.renderPlainMarkers()
        this.$nextTick(() => this.fitMapView())
        return
      }

      const clusterData = this.filteredPoints.map(point => ({
        lnglat: point.coordinate,
        extData: this.normalizePoint(point)
      }))

      try {
        this.clusterer = new this.clusterCtor(this.map, clusterData, {
          gridSize: 80,
          minClusterSize: this.clusterNum,
          averageCenter: true,
          renderMarker: context => this.renderPointMarker(context),
          renderClusterMarker: context => this.renderClusterMarker(context)
        })
      } catch (error) {
        console.error('Marker cluster render failed, fallback to plain markers:', error)
        this.renderPlainMarkers()
      }

      this.$nextTick(() => this.fitMapView())
    },

    // 聚合气泡渲染回调：根据簇内点位状态占比选择主色。
    renderClusterMarker(context) {
      if (!context || !context.marker) return

      const markers = context.markers || []
      const colorStats = {}

      markers.forEach(marker => {
        const extData = marker.getExtData ? marker.getExtData() : null
        const status = extData && extData.status
        const color = this.statusConfig[status] ? this.statusConfig[status].color : this.currentThemeConfig.color
        colorStats[color] = (colorStats[color] || 0) + 1
      })

      let color = this.currentThemeConfig.color
      let max = 0

      Object.keys(colorStats).forEach(key => {
        if (colorStats[key] > max) {
          max = colorStats[key]
          color = key
        }
      })

      const size = this.clusterSizeMap[this.clusterSize] || this.clusterSizeMap.medium
      const content = createClusterHtml({
        count: context.count,
        color,
        size,
        textColor: this.currentThemeConfig.textColor
      })

      context.marker.setOffset(new this.AMap.Pixel(-((size + 16) / 2), -((size + 16) / 2)))
      context.marker.setContent(content)
      if (context.marker.setzIndex) context.marker.setzIndex(120)
      else if (context.marker.setZIndex) context.marker.setZIndex(120)
    },
    fitMapView() {
      if (!this.map || !this.filteredPoints.length) return

      if (this.pointMarkers.length) {
        this.map.setFitView(this.pointMarkers)
        return
      }

      this.map.setFitView()
    },

    // 定位：移动地图中心到当前浏览器定位位置，并同步点位生成中心。
    locateCurrentPosition() {
      if (!navigator.geolocation) {
        this.$toast.error('当前浏览器不支持定位')
        return
      }

      navigator.geolocation.getCurrentPosition(
        position => {
          const currentPosition = [position.coords.longitude, position.coords.latitude]
          const marker = new this.AMap.Marker({
            position: currentPosition,
            offset: new this.AMap.Pixel(-8, -8),
            content: `
              <div style="
                width: 16px;
                height: 16px;
                border-radius: 50%;
                background: #1890ff;
                border: 3px solid rgba(255,255,255,0.96);
                box-shadow: 0 4px 10px rgba(24,144,255,0.35);
              "></div>
            `
          })

          this.map.setCenter(currentPosition)
          this.map.setZoom(15)
          this.centerCoordinate = currentPosition
          this.map.add(marker)
          this.$toast.success('定位成功')

          setTimeout(() => {
            if (this.map) {
              this.map.remove(marker)
            }
          }, 5000)
        },
        () => {
          this.$toast.error('定位失败，请检查浏览器定位权限')
        }
      )
    },
    getStatusText(status) {
      return this.statusConfig[status] ? this.statusConfig[status].text : status
    },
    getStatusTagType(status) {
      if (status === 'online') return 'success'
      if (status === 'offline') return 'info'
      if (status === 'warning') return 'warning'
      return ''
    }
  }
}
</script>

<style scoped lang="scss">
.point-cluster-demo {
  width: 100%;
}

.demo-layout {
  display: flex;
  gap: 18px;
  min-height: 620px;
}

.map-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.map-panel__toolbar,
.panel-card {
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 14px;
  background: var(--surface-1);
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.06);
}

.map-panel__toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;

  h3 {
    margin: 0 0 8px;
    color: #1f2d3d;
    font-size: 20px;
  }

  p {
    margin: 0;
    color: #6b778c;
    font-size: 13px;
    line-height: 1.7;
  }
}

.toolbar-copy {
  min-width: 0;
}

.toolbar-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.map-stage {
  flex: 1;
  min-height: 480px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(31, 45, 61, 0.06);
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.08);
}

.map-legend {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 14px;
  background: var(--surface-1);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #5f6b7a;
  font-size: 13px;
}

.legend-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-block;

  &.online {
    background: #4caf50;
  }

  &.offline {
    background: #909399;
  }

  &.warning {
    background: #e6a23c;
  }

  &.cluster {
    background: #3478f6;
  }
}

.side-panel {
  width: 360px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.panel-card {
  padding: 16px 18px;
}

.panel-card__title {
  margin-bottom: 14px;
  color: #1f2d3d;
  font-size: 16px;
  font-weight: 600;
}

.stats-card {
  padding-bottom: 10px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.stats-item {
  padding: 14px 12px;
  border-radius: 12px;
  background: linear-gradient(180deg, #f7fbff 0%, #eef4fb 100%);
  border: 1px solid rgba(31, 45, 61, 0.06);

  span {
    display: block;
    color: #6b778c;
    font-size: 12px;
    margin-bottom: 8px;
  }

  strong {
    color: #1f2d3d;
    font-size: 22px;
    line-height: 1;
  }

  .online {
    color: #4caf50;
  }

  .offline {
    color: #909399;
  }

  .warning {
    color: #e6a23c;
  }
}

.field-block + .field-block {
  margin-top: 18px;
}

.field-label {
  margin-bottom: 10px;
  color: #445165;
  font-size: 14px;
  font-weight: 500;
}

.field-tip {
  margin-top: 8px;
  color: #8b97a8;
  font-size: 12px;
  line-height: 1.6;
}

.detail-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  color: #303133;
  font-size: 14px;
}

.detail-label {
  color: #7a8797;
}

:deep(.amap-marker-label) {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 4px;
  padding: 4px 8px;
  box-shadow: var(--shadow-surface-1);
}

@media (max-width: 1200px) {
  .demo-layout {
    flex-direction: column;
  }

  .side-panel {
    width: 100%;
  }
}
</style>
